import { css, type Theme } from "@emotion/react";
import { typography } from "aviary-tokens";

import { dimensions } from "@styles";

export const base = css`
  font-weight: ${typography.weightBase};
  line-height: 1.125;
`;

export const titleBase = (theme: Theme) => css`
  ${base};
  font-size: ${typography.size2xlarge};

  strong {
    color: ${theme.success.textBase};
    font-weight: inherit;
  }
`;

export const paragraphBase = css`
  ${base};
  font-weight: ${typography.weightBase};
  font-size: ${typography.sizeNormal};
`;

// Modifiers
export const margin = css`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;
export const indented = css`
  margin-left: 1rem;
`;
export const fullwidth = css`
  width: 100%;
`;

// Font colors
export const isSubdued = (theme: Theme) => css`
  color: ${theme.text.subdued};
`;

// Font weights
const getBoldWeight = (isSecondaryWeight, theme: Theme) => {
  if (isSecondaryWeight) {
    return css`
      color: ${theme.text.body};
      font-weight: ${typography.weightSemiBold};
    `;
  }

  return css`
    color: ${theme.text.emphasis};
    font-weight: ${typography.weightBold};
  `;
};
const getSemiBoldWeight = (isSecondaryWeight, theme: Theme) => {
  if (isSecondaryWeight) {
    return css`
      color: ${theme.text.body};
      font-weight: ${typography.weightBase};
    `;
  }

  return css`
    color: ${theme.text.emphasis};
    font-weight: ${typography.weightSemiBold};
  `;
};

// Sizes
export const titleSizes = {
  h1: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.h1FontSize};
    line-height: ${typography.h1LineHeight};
    ${getBoldWeight(isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.mobileH1FontSize};
    }
  `,
  h2: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.h2FontSize};
    line-height: ${typography.h2LineHeight};
    ${getSemiBoldWeight(isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.mobileH2FontSize};
      line-height: 2rem;
    }
  `,
  h3: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.h3FontSize};
    line-height: ${typography.h3LineHeight};
    ${getSemiBoldWeight(isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.mobileH3FontSize};
    }
  `,
  h4: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.h4FontSize};
    line-height: ${typography.h4LineHeight};
    ${getSemiBoldWeight(isSecondaryWeight, theme)}
  `,
  h5: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.h5FontSize};
    line-height: ${typography.h5LineHeight};
    ${getSemiBoldWeight(isSecondaryWeight, theme)}
  `,
};

export const paragraphSizes = {
  body: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.bodyFontSize};
    line-height: ${typography.bodyLineHeight};
    ${getSemiBoldWeight(!isSecondaryWeight, theme)}
  `,
  footnote: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.footnoteFontSize};
    line-height: ${typography.footnoteLineHeight};
    ${getSemiBoldWeight(!isSecondaryWeight, theme)}
  `,
  caption: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.captionFontSize};
    line-height: ${typography.captionLineHeight};
    ${getSemiBoldWeight(!isSecondaryWeight, theme)}
  `,
};

export const sizeOverride = {
  h1: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.h1FontSize};
    line-height: ${typography.h1LineHeight};
    ${getBoldWeight(isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.mobileH1FontSize};
    }
  `,
  h2: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.h2FontSize};
    line-height: ${typography.h2LineHeight};
    ${getSemiBoldWeight(isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.mobileH2FontSize};
      line-height: 2rem;
    }
  `,
  h3: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.h3FontSize};
    line-height: ${typography.h3LineHeight};
    ${getSemiBoldWeight(isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.mobileH3FontSize};
    }
  `,
  h4: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.h4FontSize};
    line-height: ${typography.h4LineHeight};
    ${getSemiBoldWeight(isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.h4FontSize};
    }
  `,
  h5: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.h5FontSize};
    line-height: ${typography.h5LineHeight};
    ${getSemiBoldWeight(isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.h5FontSize};
    }
  `,
  body: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.bodyFontSize};
    line-height: ${typography.bodyLineHeight};
    ${getSemiBoldWeight(!isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.bodyFontSize};
    }
  `,
  footnote: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.footnoteFontSize};
    line-height: ${typography.footnoteLineHeight};
    ${getSemiBoldWeight(!isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.footnoteFontSize};
    }
  `,
  caption: (isSecondaryWeight, theme: Theme) => css`
    font-size: ${typography.captionFontSize};
    line-height: ${typography.captionLineHeight};
    ${getSemiBoldWeight(!isSecondaryWeight, theme)}

    @media (max-width: ${dimensions.phoneLargeMax}) {
      font-size: ${typography.captionFontSize};
    }
  `,
};
